import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Mail, Linkedin, Check, LogOut, Loader2, Sparkles, Link2, Building2, Save } from "lucide-react";
import { TeamSettings } from "@/components/TeamSettings";
import { TwoStepSettings } from "@/components/security/TwoStepSettings";

const GoogleGlyph = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

export default function Account() {
  const { user, logoutEverywhere } = useAuth();
  const [params, setParams] = useSearchParams();
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState(null);
  const [savingCo, setSavingCo] = useState(false);
  const isEmployer = user?.role === "recruiter" || user?.role === "admin";

  const saveCompany = async () => {
    setSavingCo(true);
    try { const { data } = await api.put("/company/profile", company); setCompany(data); toast.success("Company profile saved"); }
    catch { toast.error("Could not save company profile"); }
    finally { setSavingCo(false); }
  };

  const load = () => api.get("/auth/connections").then(({ data }) => setConn(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const coLoaded = useRef(false);
  useEffect(() => {
    if (!isEmployer || coLoaded.current) return;
    api.get("/company/profile")
      .then(({ data }) => { if (!coLoaded.current) { coLoaded.current = true; setCompany(data); } })
      .catch(() => { if (!coLoaded.current) { coLoaded.current = true; setCompany({}); } });
  }, [isEmployer]);
  useEffect(() => {
    if (params.get("linked")) { toast.success(`${params.get("linked") === "linkedin" ? "LinkedIn" : "Account"} connected`); params.delete("linked"); setParams(params, { replace: true }); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGoogle = () => {
    sessionStorage.setItem("hs_link_mode", "google");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth/social")}`;
  };
  const connectLinkedIn = () => { if (!user?.id) return; window.location.href = `${API}/auth/linkedin/login?link_uid=${user.id}`; };

  const signOutEverywhere = async () => {
    setBusy(true);
    const res = await logoutEverywhere();
    setBusy(false);
    if (res.ok) toast.success("Signed out of all other devices");
    else toast.error(res.error);
  };

  const Provider = ({ icon, name, desc, connected, onConnect, disabled, testid }) => (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">{icon}</div>
        <div className="min-w-0"><div className="font-medium text-sm">{name}</div><div className="text-xs text-muted-foreground truncate">{desc}</div></div>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 shrink-0" data-testid={`${testid}-connected`}><Check className="h-3.5 w-3.5" /> Connected</span>
      ) : disabled ? (
        <span className="text-xs text-muted-foreground shrink-0">Unavailable</span>
      ) : (
        <Button variant="outline" size="sm" onClick={onConnect} data-testid={`${testid}-connect`} className="shrink-0 gap-1.5"><Link2 className="h-3.5 w-3.5" /> Connect</Button>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="account-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Account & security</div>
        <h1 className="text-3xl font-display font-bold tracking-tight">{user?.name}</h1>
        <p className="text-sm text-muted-foreground mt-1"><span className="capitalize">{user?.role}</span> · {user?.email}</p>
      </div>

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> Sign-in methods</h2>
        {!conn ? <div className="flex items-center gap-2 text-muted-foreground text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
          <div className="space-y-3">
            <Provider testid="conn-email" icon={<Mail className="h-5 w-5 text-primary" />} name="Email & password" desc={conn.email ? conn.primary_email : "Not set — you signed up socially"} connected={conn.email} disabled />
            <Provider testid="conn-google" icon={<GoogleGlyph />} name="Google" desc={conn.google ? "Sign in with Google" : "Link your Google account for one-tap sign-in"} connected={conn.google} onConnect={connectGoogle} />
            <Provider testid="conn-linkedin" icon={<Linkedin className="h-5 w-5 text-[#0A66C2]" />} name="LinkedIn" desc={conn.linkedin ? "Sign in with LinkedIn" : conn.linkedin_available ? "Link your LinkedIn account" : "Not enabled on this workspace yet"} connected={conn.linkedin} onConnect={connectLinkedIn} disabled={!conn.linkedin_available && !conn.linkedin} />
          </div>
        )}
      </section>

      {isEmployer && (
        <section className="space-y-3" data-testid="company-profile-section">
          <h2 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground"><Building2 className="h-3.5 w-3.5 text-primary" /> Company profile</h2>
          {company === null ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <div className="space-y-1.5" data-testid="company-completion">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono uppercase tracking-wider text-muted-foreground">Profile completion</span>
                  <span className="font-semibold text-foreground" data-testid="company-completion-pct">{company.completion}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted/50 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500" style={{ width: `${company.completion}%` }} />
                </div>
              </div>
              {!company.complete && (
                <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="company-incomplete">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" /> {company.completion}% complete — a full profile brands your careers page, candidate emails and reports.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Company name</Label><Input value={company.company_name || ""} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} placeholder="Acme Corp" data-testid="company-name" /></div>
                <div className="space-y-1.5"><Label>Website</Label><Input value={company.company_website || ""} onChange={(e) => setCompany((c) => ({ ...c, company_website: e.target.value }))} placeholder="https://acme.com" data-testid="company-website" /></div>
                <div className="space-y-1.5"><Label>Industry</Label><Input value={company.company_industry || ""} onChange={(e) => setCompany((c) => ({ ...c, company_industry: e.target.value }))} placeholder="Software" data-testid="company-industry" /></div>
                <div className="space-y-1.5"><Label>Company size</Label><Input value={company.company_size || ""} onChange={(e) => setCompany((c) => ({ ...c, company_size: e.target.value }))} placeholder="51-200" data-testid="company-size" /></div>
                <div className="space-y-1.5"><Label>Location</Label><Input value={company.company_location || ""} onChange={(e) => setCompany((c) => ({ ...c, company_location: e.target.value }))} placeholder="Muscat, Oman" data-testid="company-location" /></div>
                <div className="space-y-1.5"><Label>Logo URL</Label><Input value={company.brand_logo_url || ""} onChange={(e) => setCompany((c) => ({ ...c, brand_logo_url: e.target.value }))} placeholder="https://…/logo.png" data-testid="company-logo" /></div>
              </div>
              <div className="space-y-1.5"><Label>About</Label><Textarea rows={3} value={company.company_about || ""} onChange={(e) => setCompany((c) => ({ ...c, company_about: e.target.value }))} placeholder="What your company does and why great people join…" data-testid="company-about" /></div>
              <div className="flex justify-end"><Button onClick={saveCompany} disabled={savingCo} data-testid="company-save" className="gap-1.5">{savingCo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save company profile</Button></div>
            </div>
          )}
        </section>
      )}

      <TeamSettings />

      <section className="space-y-3">
        <h2 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Session security</h2>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div><div className="font-medium text-sm">Sign out everywhere</div><div className="text-xs text-muted-foreground">End every other session on all devices. This one stays active.</div></div>
          <Button variant="outline" size="sm" onClick={signOutEverywhere} disabled={busy} data-testid="signout-everywhere" className="shrink-0 gap-1.5 text-rose-400 hover:text-rose-300 border-rose-500/30">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />} Sign out others</Button>
        </div>
        <TwoStepSettings />
      </section>
    </div>
  );
}
